import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import useMetaTags from '../../hooks/useMetaTags';
import PageHero from '../../components/PageHero/PageHero';
import Footer from '../../components/Footer/Footer';
import AnimationSection from '../../components/AnimationSection/AnimationSection';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import AgricultureOutlinedIcon from '@mui/icons-material/AgricultureOutlined';

const SectionContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#fdfcfa',
  padding: '60px 0 80px 0',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, rgba(180, 160, 130, 0.03) 0%, rgba(69, 91, 64, 0.05) 100%)',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#455b40',
  fontWeight: 400,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  fontSize: '0.9rem',
  marginBottom: '24px',
  [theme.breakpoints.down('md')]: {
    fontSize: '0.8rem',
    marginBottom: '18px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    marginBottom: '16px'
  }
}));

const MainHeading = styled(Typography)(({ theme }) => ({
  color: '#000000',
  fontWeight: 300,
  lineHeight: 1.2,
  marginBottom: '32px',
  fontSize: '3rem',
  letterSpacing: '1px',
  fontFamily: '"Playfair Display", serif',
  [theme.breakpoints.down('lg')]: {
    fontSize: '2.5rem',
    marginBottom: '28px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '2rem',
    marginBottom: '24px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.75rem',
    marginBottom: '20px',
    lineHeight: 1.3
  }
}));

const ContentText = styled(Typography)(({ theme }) => ({
  color: '#333333',
  lineHeight: 1.8,
  fontSize: '1.1rem',
  marginBottom: '24px',
  [theme.breakpoints.down('md')]: {
    fontSize: '1rem',
    marginBottom: '20px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.95rem',
    marginBottom: '16px',
    lineHeight: 1.7
  }
}));

const BulletPoint = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2.5),
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.spacing(2),
  background: 'rgba(69, 91, 64, 0.03)',
  border: '1px solid rgba(69, 91, 64, 0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(69, 91, 64, 0.06)',
    transform: 'translateX(4px)',
    borderColor: 'rgba(69, 91, 64, 0.15)',
  },
}));

const ContentCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  borderRadius: '20px',
  padding: theme.spacing(5),
  boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
  border: '1px solid rgba(69, 91, 64, 0.08)',
  position: 'relative',
  zIndex: 2,
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  }
}));

const IconBox = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #455b40 0%, #6c8c68 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  boxShadow: '0 8px 24px rgba(69, 91, 64, 0.2)',
}));


const VisionMission: React.FC = () => {
  // Update meta tags for mission vision page
  useMetaTags({
    title: 'Misión y Visión - EKKOFOOD Agricultura Sostenible',
    description: 'Conoce nuestra misión y visión en EKKOFOOD. Transformamos la agricultura hacia un futuro sostenible, promoviendo la salud del suelo y la alimentación ecológica accesible.',
    image: 'https://ekkofood.com/vission.png',
    url: 'https://ekkofood.com/mission-vision',
    type: 'website'
  });

  return (
    <>
      <PageHero 
        title="Misión y Visión"
        subtitle="Ekkofood - Agricultura Ecológica"
        description="Transformando la agricultura hacia un futuro sostenible"
        backgroundImage="/vission.png"
        height="400px"
        showButton={false}
        contentAlign="center"
        overlay={true}
        overlayOpacity={0.4}
        showBreadcrumbs={true}
        breadcrumbs={[
          { label: "Inicio", link: "/" },
          
          { label: "Misión y Visión", link: "/mission-vision" }
        ]}
      />
      
      <SectionContainer>
        <Container maxWidth="lg">
          {/* Vision Section */}
          <Box sx={{ mb: 10 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 4, md: 6, lg: 8 }
            }}>
              {/* Vision Image */}
              <Box sx={{ 
                flex: 1,
                maxWidth: { md: '45%' },
                width: '100%'
              }}>
                <Box
                  sx={{
                    position: 'relative',
                    height: { 
                      xs: '300px', 
                      sm: '350px',
                      md: '400px',
                      lg: '450px'
                    },
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
                  }}
                >
                  <Box
                    component="img"
                    src="https://images.unsplash.com/photo-1500595046743-cd271d694d30?q=80&w=2074"
                    alt="Visión de agricultura sostenible"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: 'brightness(1.1) contrast(1.05)',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  />
                  
                  {/* Overlay gradient */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(135deg, rgba(69, 91, 64, 0.1) 0%, transparent 50%)',
                    }}
                  />
                  
                  {/* Icon badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 30,
                      right: 30,
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '50%',
                      p: 2,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                    }}
                  >
                    <RemoveRedEyeOutlinedIcon sx={{ fontSize: 32, color: '#455b40' }} />
                  </Box>
                </Box>
              </Box>

              {/* Vision Content */}
              <Box sx={{ 
                flex: 1, 
                position: 'relative', 
                zIndex: 2,
                maxWidth: { md: '55%' }
              }}>
                <ContentCard>
                  <SectionTitle>
                    NUESTRA VISIÓN
                  </SectionTitle>
                  
                  <MainHeading>
                    Un mundo donde la agricultura<br />
                    ecológica sea la norma
                  </MainHeading>
                  
                  <ContentText sx={{ mb: 3 }}>
                    Soñamos con un mundo donde la <strong style={{ color: '#455b40' }}>agricultura ecológica</strong> sea la norma y no la excepción; 
                    donde cada agricultor tenga la capacidad de producir sus propios <strong style={{ color: '#455b40' }}>biofertilizantes y 
                    biopesticidas</strong>, y donde los consumidores puedan acceder a <strong style={{ color: '#455b40' }}>alimentos sanos y asequibles</strong>.
                  </ContentText>
                  
                  <Box sx={{ 
                    pl: 3, 
                    borderLeft: '3px solid #455b40',
                    ml: 2,
                    mb: 4
                  }}>
                    <ContentText sx={{ 
                      fontStyle: 'italic',
                      color: '#555',
                      mb: 0
                    }}>
                      Queremos ser un referente internacional en <strong style={{ color: '#455b40' }}>formación agrícola sostenible</strong>, inspirando 
                      a nuevas generaciones a cultivar con respeto hacia la tierra, las comunidades y el futuro.
                    </ContentText>
                  </Box>

                  <Button
                    variant="contained"
                    href="/cursos"
                    sx={{
                      backgroundColor: '#455b40',
                      color: 'white',
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 500,
                      borderRadius: '50px',
                      textTransform: 'none',
                      letterSpacing: '0.5px',
                      boxShadow: '0 8px 25px rgba(69, 91, 64, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#6c8c68',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 35px rgba(69, 91, 64, 0.4)',
                      },
                    }}
                  >
                    Ver Nuestros Cursos
                  </Button>
                </ContentCard>
              </Box>
            </Box>
          </Box>

          {/* Mission Section */}
          <Box>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexDirection: { xs: 'column-reverse', md: 'row' },
              gap: { xs: 4, md: 6, lg: 8 }
            }}>
              {/* Mission Content */}
              <Box sx={{ 
                flex: 1, 
                position: 'relative', 
                zIndex: 2,
                maxWidth: { md: '55%' }
              }}>
                <ContentCard>
                  <SectionTitle>
                    NUESTRA MISIÓN
                  </SectionTitle>
                  
                  <MainHeading>
                    Difundir conocimiento<br />
                    práctico y accesible
                  </MainHeading>
                  
                  <ContentText sx={{ mb: 4 }}>
                    Nuestra misión es <strong style={{ color: '#455b40' }}>difundir conocimiento práctico y accesible</strong> en agricultura orgánica y 
                    regenerativa, empoderando a agricultores y organizaciones para que:
                  </ContentText>

                  <Box sx={{ mb: 4 }}>
                    <BulletPoint>
                      <CheckCircleIcon sx={{ color: '#455b40', mr: 2, mt: 0.2, flexShrink: 0 }} />
                      <Typography variant="body1" sx={{ lineHeight: 1.6, fontSize: '1rem' }}>
                        Produzcan de manera independiente sus propios <strong>biofertilizantes y biopesticidas</strong>.
                      </Typography>
                    </BulletPoint>
                    <BulletPoint>
                      <CheckCircleIcon sx={{ color: '#455b40', mr: 2, mt: 0.2, flexShrink: 0 }} />
                      <Typography variant="body1" sx={{ lineHeight: 1.6, fontSize: '1rem' }}>
                        Reduzcan <strong>costes de producción</strong> sin sacrificar la calidad ni la sostenibilidad.
                      </Typography>
                    </BulletPoint>
                    <BulletPoint>
                      <CheckCircleIcon sx={{ color: '#455b40', mr: 2, mt: 0.2, flexShrink: 0 }} />
                      <Typography variant="body1" sx={{ lineHeight: 1.6, fontSize: '1rem' }}>
                        Generen <strong>alimentos más saludables</strong> y justos para toda la sociedad.
                      </Typography>
                    </BulletPoint>
                    <BulletPoint>
                      <CheckCircleIcon sx={{ color: '#455b40', mr: 2, mt: 0.2, flexShrink: 0 }} />
                      <Typography variant="body1" sx={{ lineHeight: 1.6, fontSize: '1rem' }}>
                        Contribuyan a <strong>restaurar los ecosistemas</strong> y regenerar los suelos.
                      </Typography>
                    </BulletPoint>
                  </Box>

                  <Box 
                    sx={{ 
                      p: 3, 
                      borderRadius: 2, 
                      background: 'linear-gradient(135deg, rgba(69, 91, 64, 0.05) 0%, rgba(69, 91, 64, 0.02) 100%)',
                      border: '1px solid rgba(69, 91, 64, 0.1)'
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        lineHeight: 1.7, 
                        color: '#555',
                        fontSize: '0.95rem',
                        fontStyle: 'italic'
                      }}
                    >
                      En Ekkofood creemos que cada finca, cada agricultor y cada decisión cuentan para 
                      construir un futuro más <strong style={{ color: '#455b40' }}>resiliente, justo y sostenible</strong>, en sintonía con los valores de 
                      movimientos como <strong style={{ color: '#455b40' }}>Slow Food</strong>, que promueven alimentos buenos, limpios y justos para 
                      todos.
                    </Typography>
                  </Box>
                </ContentCard>
              </Box>

              {/* Mission Images - Stacked */}
              <Box sx={{ 
                flex: 1,
                maxWidth: { md: '45%' },
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 3
              }}>
                {/* Top Image */}
                <Box
                  sx={{
                    position: 'relative',
                    height: { 
                      xs: '220px', 
                      sm: '260px',
                      md: '300px',
                      lg: '350px'
                    },
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 15px 40px rgba(0,0,0,0.1)'
                  }}
                >
                  <Box
                    component="img"
                    src="https://images.unsplash.com/photo-1464226184884-fa280b87c399?q=80&w=2070"
                    alt="Agricultura regenerativa"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: 'brightness(1.1) contrast(1.05)',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  />
                  
                  {/* Overlay gradient */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(135deg, rgba(69, 91, 64, 0.1) 0%, transparent 50%)',
                    }}
                  />
                  
                  {/* Icon badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 20,
                      left: 20,
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '50%',
                      p: 1.5,
                      boxShadow: '0 6px 20px rgba(0,0,0,0.1)'
                    }}
                  >
                    <AgricultureOutlinedIcon sx={{ fontSize: 24, color: '#455b40' }} />
                  </Box>
                </Box>

                {/* Bottom Image */}
                <Box
                  sx={{
                    position: 'relative',
                    height: { 
                      xs: '220px', 
                      sm: '260px',
                      md: '300px',
                      lg: '350px'
                    },
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 15px 40px rgba(0,0,0,0.1)'
                  }}
                >
                  <Box
                    component="img"
                    src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?q=80&w=2070"
                    alt="Producción de biofertilizantes"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: 'brightness(1.1) contrast(1.05)',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  />
                  
                  {/* Overlay gradient */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(135deg, rgba(69, 91, 64, 0.1) 0%, transparent 50%)',
                    }}
                  />

                  {/* Stat badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 20,
                      right: 20,
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '12px',
                      p: 2,
                      minWidth: '120px',
                      textAlign: 'center'
                    }}
                  >
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        color: '#455b40', 
                        fontWeight: 700, 
                        mb: 0.5,
                        fontSize: '1.5rem'
                      }}
                    >
                      100%
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666', 
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        lineHeight: 1.3
                      }}
                    >
                      Agricultura<br />Ecológica
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </SectionContainer>

      <AnimationSection />
      
      <Footer />
    </>
  );
};

export default VisionMission;